import React from 'react'
import { COLORS, eur, num, pct, stepLabel } from './format'
import { Term } from './Glossary'

const cell = { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }
const cellL = { ...cell, textAlign: 'left' }

const Dot = ({ c }) => (
  <span
    style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      borderRadius: 2,
      background: c,
      marginRight: 6
    }}
  />
)

/**
 * "Qué se cobra": reparte TODOS los mensajes enviados del periodo en sus conceptos de cobro,
 * de modo que la suma es exactamente el total de enviados (cuadre por fecha de envío).
 */
// Sub-filas "de dónde sale": mensajes cobrados sueltos agrupados por paso del flujo.
const StepRows = ({ byStep, field }) =>
  Object.entries(byStep || {})
    .filter(([, v]) => v[field] > 0)
    .sort((a, b) => b[1][field] - a[1][field])
    .map(([step, v]) => (
      <tr key={`${field}-${step}`} style={{ color: '#64748b', fontSize: 12 }}>
        <td style={{ ...cellL, paddingLeft: 28 }}>
          ↳ {step === 'init' ? 'Mensaje inicial sin respuesta en 24 h' : stepLabel(step)}
        </td>
        <td style={cell}>{num(v[field])}</td>
        <td style={cell} />
        <td style={cell} />
        <td style={cell} />
      </tr>
    ))

const ChargeBreakdown = ({ totals, rates }) => {
  const sent = totals.funnel ? totals.funnel.sent : 0
  const openers = totals.openersSent || 0
  const parts = [
    { key: 'simple', v: totals.simple, c: COLORS.simple },
    { key: 'basico', v: totals.basico, c: COLORS.basico },
    { key: 'conversacion', v: openers, c: COLORS.conversacion },
    { key: 'incluido', v: totals.bundled, c: COLORS.incluido }
  ]
  const first = totals.firstSessions || 0
  const extra = totals.extraSessions || 0
  const units = totals.simple + totals.basico + first + extra
  const edge = openers - (first + extra)

  return (
    <div>
      <div style={{ display: 'flex', height: 16, borderRadius: 6, overflow: 'hidden', marginBottom: 6 }}>
        {parts.map((p) =>
          p.v > 0 ? (
            <div
              key={p.key}
              title={`${num(p.v)} mensajes (${pct(p.v, sent)})`}
              style={{ width: `${(p.v / (sent || 1)) * 100}%`, background: p.c }}
            />
          ) : null
        )}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        {num(sent)} mensajes enviados: {pct(totals.simple, sent)} Simple ·{' '}
        {pct(openers, sent)} abren sesión · {pct(totals.bundled, sent)} van incluidos gratis
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ color: '#64748b', fontSize: 12 }}>
            <th style={cellL}>Concepto</th>
            <th style={cell}>Mensajes enviados</th>
            <th style={cell}>Se cobran</th>
            <th style={cell}>Precio</th>
            <th style={cell}>Coste</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cellL}>
              <Dot c={COLORS.simple} />
              <Term id="simple" />
            </td>
            <td style={cell}>{num(totals.simple)}</td>
            <td style={cell}>{num(totals.simple)}</td>
            <td style={cell}>{eur(rates.simple, 3)}</td>
            <td style={cell}>{eur(totals.simple * rates.simple)}</td>
          </tr>
          <StepRows byStep={totals.byStep} field="simple" />
          <tr>
            <td style={cellL}>
              <Dot c={COLORS.basico} />
              <Term id="basico" />
            </td>
            <td style={cell}>{num(totals.basico)}</td>
            <td style={cell}>{num(totals.basico)}</td>
            <td style={cell}>{eur(rates.basico, 3)}</td>
            <td style={cell}>{eur(totals.basico * rates.basico)}</td>
          </tr>
          <StepRows byStep={totals.byStep} field="basico" />
          <tr>
            <td style={cellL}>
              <Dot c={COLORS.conversacion} />
              Abren sesión
            </td>
            <td style={cell}>{num(openers)}</td>
            <td style={cell} />
            <td style={cell} />
            <td style={cell} />
          </tr>
          <tr style={{ color: '#475569' }}>
            <td style={{ ...cellL, paddingLeft: 28 }}>
              <Term id="conversacion">↳ Conversación (1ª sesión)</Term>
            </td>
            <td style={cell} />
            <td style={cell}>{num(first)}</td>
            <td style={cell}>{eur(rates.conversacional, 3)}</td>
            <td style={cell}>{eur(first * rates.conversacional)}</td>
          </tr>
          <tr style={{ color: '#475569' }}>
            <td style={{ ...cellL, paddingLeft: 28 }}>
              <Term id="extra">↳ Sesión extra</Term>
            </td>
            <td style={cell} />
            <td style={cell}>{num(extra)}</td>
            <td style={cell}>{eur(rates.conversacional, 3)}</td>
            <td style={cell}>{eur(extra * rates.conversacional)}</td>
          </tr>
          <tr>
            <td style={cellL}>
              <Dot c={COLORS.incluido} />
              <Term id="incluido">Incluido en sesión</Term>
            </td>
            <td style={cell}>{num(totals.bundled)}</td>
            <td style={cell}>0</td>
            <td style={cell}>{eur(0)}</td>
            <td style={cell}>{eur(0)}</td>
          </tr>
          <tr style={{ fontWeight: 700 }}>
            <td style={cellL}>Total</td>
            <td style={cell}>{num(sent)}</td>
            <td style={cell}>{num(units)}</td>
            <td style={cell} />
            <td style={cell}>{eur(totals.cost)}</td>
          </tr>
        </tbody>
      </table>
      {edge !== 0 && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
          Las sesiones se cobran el día en que el usuario interactúa, no el del envío. Por eso hay{' '}
          {num(Math.abs(edge))} de diferencia entre los mensajes que abren sesión y las sesiones
          cobradas: son sesiones que cruzan el inicio o el final del periodo.
        </div>
      )}
    </div>
  )
}

export default ChargeBreakdown
